import {
    LIST_INIT,
    LIST_SUCCESS,
    LIST_FAILED,
    LIST_REFRESH,
    LIST_REFRESHING,
    LIST_UNLOAD,
    LIST_CLEAR,
    LIST_CLEAR_FILTER,
    LIST_UPDATING,
    LIST_SAVED,
    LIST_DELETED,
    LIST_FILTERED,
    LIST_SETVISIBLES,
    LIST_SETPAGE,
    LIST_FIRST_PAGE,
    LIST_PREVIOUS_PAGE,
    LIST_NEXT_PAGE,
    LIST_LAST_PAGE,
    LIST_SELECT_ONE,
    LIST_SELECT_ALL,
    LIST_SELECT_SOME,
    LIST_REORDER,
    LIST_SERVER_SUCCESS,
    LIST_SERVER_STATE
} from './constants.js';

import config from 'settings/environment';

import {
    getList,
    getListApi,
    getFiltersList,
    getListParameters,
    isListInit,
    selectedList,
    getToken
} from '../reducer';

import { pausa, getOperandos } from 'core/helpers';

// ============================================================
// Gestion de peticiones servidor (AbortController + debounce)
// ============================================================

const abortControllers = new Map();
const debounceTimers = new Map();
const DEBOUNCE_MS = 300;

const cancelPending = entidad => {
    const controller = abortControllers.get(entidad);
    if (controller) controller.abort();
};

const debouncedFetch = (entidad, fn) => {
    clearTimeout(debounceTimers.get(entidad));
    debounceTimers.set(entidad, setTimeout(fn, DEBOUNCE_MS));
};

// ============================================================
// Construccion de query params para modo servidor
// ============================================================

const buildServerQuery = entityState => {
    const params = new URLSearchParams();

    // Paginacion (serverPage = indice del chunk)
    params.set('page', String((entityState.serverPage || 0) + 1)); // 0-based -> 1-based
    params.set('pageSize', String(entityState.serverPageSize));

    // Filtros
    buildFilterParams(entityState.filters, params);

    // Sort
    if (entityState.serverSort) {
        entityState.serverSort.forEach(s => params.append('sort', s));
    }

    // Search
    if (entityState.serverSearch) {
        params.set('search', entityState.serverSearch);
    }

    return params;
};

const buildTotalsQuery = entityState => {
    const params = new URLSearchParams();

    // Filtros (mismos que la peticion de datos)
    buildFilterParams(entityState.filters, params);

    // Search
    if (entityState.serverSearch) {
        params.set('search', entityState.serverSearch);
    }

    // Agregados
    if (entityState.aggregates) {
        entityState.aggregates.forEach(agg => {
            const opName =
                typeof agg.op === 'string' ? agg.op.toLowerCase() : 'sum';
            params.append('agg', `${opName}:${agg.field}`);
        });
    }

    return params;
};

const buildFilterParams = (filters, params) => {
    for (const key in filters) {
        const { filtro, modificador } = filters[key];

        // Saltamos filtros con modificador negado (no soportado en servidor)
        if (modificador === '!') continue;

        filtro.forEach(f => {
            if (f.campo === '' || f.valor === '' || f.valor === null) return;

            const fieldName = Array.isArray(f.campo)
                ? f.campo.flat(Infinity).join('.')
                : f.campo;

            if (f.op === 'in') {
                // Multi-valor (IN)
                if (Array.isArray(f.valor)) {
                    f.valor.forEach(v => params.append(fieldName, String(v)));
                } else {
                    params.append(fieldName, String(f.valor));
                }
            } else if (f.op === 'between') {
                // Rango: valor puede ser "5*10" o "25/5*31/5"
                const isDate = ['date', 'datetime-local', 'month'].includes(
                    (f.tipo || '').toLowerCase()
                );

                if (isDate) {
                    const operandos = getOperandos(f.valor, f.tipo);
                    if (operandos.desde)
                        params.append(fieldName, `gte:${operandos.desde}`);
                    if (operandos.hasta)
                        params.append(fieldName, `lte:${operandos.hasta}`);
                    if (operandos.value)
                        params.append(fieldName, operandos.value);
                } else {
                    const parts = String(f.valor).split('*');
                    if (parts.length >= 2) {
                        const desde = parts[0].trim();
                        const hasta = parts[1].trim();
                        if (desde) params.append(fieldName, `gte:${desde}`);
                        if (hasta) params.append(fieldName, `lte:${hasta}`);
                    } else if (parts[0].trim()) {
                        params.append(fieldName, parts[0].trim());
                    }
                }
            } else if (f.op === '' && f.tipo === 'text') {
                // Contains -> wildcard
                params.append(fieldName, `*${f.valor}*`);
            } else {
                // Exacto
                params.append(fieldName, String(f.valor));
            }
        });
    }
};

// ============================================================
// Fetch paginado desde servidor
// ============================================================

const fetchServerPage =
    (entidad, includeTotals, clientPage, skipRefreshing) =>
    (dispatch, getState) => {
        cancelPending(entidad);

        const controller = new AbortController();
        abortControllers.set(entidad, controller);

        if (!skipRefreshing) {
            dispatch({ type: LIST_REFRESHING, payload: { entidad } });
        }

        const token = getToken(getState());
        const entityState = getState().list[entidad];
        const api = entityState.api;

        const dataParams = buildServerQuery(entityState);
        const dataUrl = `${config.servidor}:${config.puerto}/api/${api}?${dataParams.toString()}`;

        const requests = [
            fetch(dataUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + token
                },
                signal: controller.signal
            }).then(r => r.json())
        ];

        if (includeTotals) {
            const totalsParams = buildTotalsQuery(entityState);
            const totalsUrl = `${config.servidor}:${config.puerto}/api/${api}/totals?${totalsParams.toString()}`;

            requests.push(
                fetch(totalsUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token
                    },
                    signal: controller.signal
                }).then(r => r.json())
            );
        }

        Promise.all(requests)
            .then(results => {
                const data = results[0];
                const totals = results.length > 1 ? results[1] : null;

                if (data.code !== undefined) {
                    dispatch({
                        type: LIST_FAILED,
                        payload: { error: data, entidad }
                    });
                } else {
                    dispatch({
                        type: LIST_SERVER_SUCCESS,
                        payload: { entidad, data, totals, clientPage }
                    });
                }
            })
            .catch(error => {
                if (error.name === 'AbortError') return;
                dispatch({
                    type: LIST_FAILED,
                    payload: {
                        error: {
                            code: 503,
                            message: 'El servicio no esta disponible'
                        },
                        entidad
                    }
                });
            });
    };

// ============================================================
// Acciones existentes (modificadas para soporte servidor)
// ============================================================

export const listEntity =
    (entidad, api, selector, action, aggregates, options, defaultFilters) =>
    (dispatch, getState) => {
        const empty = getList(entidad, getState()).length === 0;

        if (empty && !isListInit(entidad, getState())) {
            const serverPaged = options && options.serverPaged;
            const pageSize = options && options.pageSize;
            const visibles = options && options.visibles;

            // accion para el reducer
            dispatch({
                type: LIST_INIT,
                payload: {
                    aggregates,
                    entidad,
                    api,
                    serverPaged,
                    pageSize,
                    defaultFilters,
                    visibles
                }
            });

            if (serverPaged) {
                // Modo servidor: fetch paginado + totales (con filtros ya aplicados)
                // skipRefreshing=true porque LIST_INIT ya establece isReady:false
                dispatch(fetchServerPage(entidad, true, 0, true));

                // Ejecuto la accion posterior si existe
                // Se ejecutara cuando LIST_SERVER_SUCCESS se dispare
                // Por ahora no podemos garantizar el callback con datos
                action && action([]);
            } else {
                // Modo cliente: comportamiento actual
                const token = getToken(getState());

                fetch(`${config.servidor}:${config.puerto}/api/${api}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token
                    }
                })
                    .then(response => {
                        return response.json();
                    })
                    .then(
                        data => {
                            if (data.code !== undefined) {
                                dispatch({
                                    type: LIST_FAILED,
                                    payload: { error: data, entidad }
                                });
                            } else {
                                dispatch({
                                    type: LIST_SUCCESS,
                                    payload: { data, entidad }
                                });

                                if (selector !== undefined) {
                                    const lista = data
                                        .filter(tarea => tarea[selector])
                                        .map(tarea => tarea.id);
                                    dispatch({
                                        type: LIST_SELECT_SOME,
                                        payload: {
                                            entidad,
                                            data: lista
                                        }
                                    });
                                }

                                action && action(data);
                            }
                        },
                        error =>
                            dispatch({
                                type: LIST_FAILED,
                                payload: {
                                    error: {
                                        code: 503,
                                        message:
                                            'El servicio no esta disponible'
                                    },
                                    entidad
                                }
                            })
                    );
            }
        }
    };

export const refreshList =
    (entidad, inicializa, newApi, data, action) => (dispatch, getState) => {
        const entityState = getState().list[entidad];

        if (entityState && entityState.serverPaged && data === undefined) {
            // Modo servidor: re-fetch desde servidor preservando página actual
            dispatch(fetchServerPage(entidad, true, entityState.page));
            action && action();
            return;
        }

        if (data === undefined) {
            let duration = Date.now();
            dispatch({ type: LIST_REFRESHING, payload: { entidad } });

            const api =
                newApi === undefined ? getListApi(entidad, getState()) : newApi;

            // Obtengo el token
            const token = getToken(getState());

            // accion para el servidor
            fetch(`${config.servidor}:${config.puerto}/api/${api}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + token
                }
            })
                .then(response => {
                    return response.json();
                })
                .then(
                    data => {
                        duration = Date.now() - duration;
                        //console.log('Tiempo Refresco', duration / 1000);
                        if (data.code !== undefined) {
                            dispatch({
                                type: LIST_FAILED,
                                payload: { error: data, entidad }
                            });
                        } else {
                            dispatch({
                                type: LIST_REFRESH,
                                payload: {
                                    data,
                                    entidad,
                                    api,
                                    inicializa
                                }
                            });
                            action && action();
                        }
                    },
                    error =>
                        dispatch({
                            type: LIST_FAILED,
                            payload: {
                                error: {
                                    code: 503,
                                    message: 'El servicio no esta disponible'
                                },
                                entidad
                            }
                        })
                );
        } else {
            // Ya tengo los datos cambio el estado
            dispatch({
                type: LIST_REFRESH,
                payload: { data, entidad, inicializa }
            });
            action && action();
        }
    };

export const deleteSelectedList =
    (entidad, id, action) => (dispatch, getState) => {
        const seleccionados =
            id === undefined ? selectedList(entidad, getState()) : [id];

        // accion para el reducer
        dispatch({ type: LIST_UPDATING, payload: { entidad } });

        // Obtengo el token
        const token = getToken(getState());

        const api = getListApi(entidad, getState());

        // accion para el servidor
        fetch(`${config.servidor}:${config.puerto}/api/${api}`, {
            method: 'DELETE',
            body: JSON.stringify(seleccionados),
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
            .then(response => {
                return response.json();
            })
            .then(
                data => {
                    if (data.code !== undefined) {
                        dispatch({
                            type: LIST_FAILED,
                            payload: { error: data, entidad }
                        });
                    } else {
                        dispatch({
                            type: LIST_DELETED,
                            payload: { entidad, seleccionados }
                        });
                        action && action();
                    }
                },
                error => {
                    dispatch({
                        type: LIST_FAILED,
                        payload: {
                            error: {
                                code: 503,
                                message: 'El servicio no esta disponible'
                            },
                            entidad
                        }
                    });
                }
            );
    };

export const updateList =
    (entidad, news, updSel, updUnSel, action) => (dispatch, getState) => {
        const seleccionados = selectedList(entidad, getState());
        const noSeleccionados = [];

        const api = getListApi(entidad, getState());

        getList(entidad, getState()).map(
            item =>
                !seleccionados.includes(item.id) &&
                noSeleccionados.push(item.id)
        );

        // accion para el reducer
        dispatch({ type: LIST_UPDATING, payload: { entidad } });

        // Obtengo el token
        const token = getToken(getState());

        // Creo el objeto de actualizacion
        const parche = {
            seleccionados: seleccionados,
            noSeleccionados: noSeleccionados,
            news: news ? (Array.isArray(news) ? news : [news]) : [],
            updSel: updSel,
            updUnSel: updUnSel
        };

        // accion para el servidor
        fetch(`${config.servidor}:${config.puerto}/api/${api}`, {
            method: 'POST',
            body: JSON.stringify(parche),
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (data.code !== undefined) {
                    dispatch({
                        type: LIST_FAILED,
                        payload: { error: data, entidad }
                    });
                } else {
                    dispatch({
                        type: LIST_SAVED,
                        payload: { data, entidad }
                    });
                    action && action();
                }
            })
            .catch(error => {
                dispatch({
                    type: LIST_FAILED,
                    payload: {
                        error: {
                            code: 503,
                            message: 'El servicio no esta disponible'
                        },
                        entidad
                    }
                });
            });
    };

export const filterList =
    (entidad, campos, data, op, type, modificador, reset, nested) =>
    (dispatch, getState) => {
        // Construimos el filtro para comparar con el actual
        const entityState = getState().list[entidad];
        if (entityState) {
            const nombreFiltro = campos + op;
            const prevFilter = entityState.filters[nombreFiltro];

            if (data !== '' && data !== null && prevFilter) {
                // Comparar si el filtro ya aplicado es identico
                const newFiltro = campos.map(item => ({
                    campo: item,
                    valor: data,
                    op: op,
                    tipo: type,
                    nested: nested
                }));
                const newEntry = {
                    modificador: modificador,
                    filtro: newFiltro
                };
                if (JSON.stringify(prevFilter) === JSON.stringify(newEntry))
                    return;
            }
        }

        dispatch({
            type: LIST_FILTERED,
            payload: {
                entidad,
                campos,
                data,
                op,
                type,
                modificador,
                reset,
                nested
            }
        });

        // En modo servidor, lanzar fetch con debounce
        if (entityState && entityState.serverPaged) {
            debouncedFetch(entidad, () => {
                dispatch(fetchServerPage(entidad, true, 0));
            });
        }
    };

export const clearList = entidad => ({
    type: LIST_CLEAR,
    payload: { entidad }
});

export const clearFilter = (entidad, filter) => (dispatch, getState) => {
    if (Object.keys(getFiltersList(entidad, getState())).includes(filter)) {
        dispatch({
            type: LIST_CLEAR_FILTER,
            payload: { entidad, filter }
        });

        // En modo servidor, re-fetch
        const entityState = getState().list[entidad];
        if (entityState && entityState.serverPaged) {
            dispatch(fetchServerPage(entidad, true, 0));
        }
    }
};

export const unloadList = entidad => (dispatch, getState) => {
    cancelPending(entidad);
    clearTimeout(debounceTimers.get(entidad));
    abortControllers.delete(entidad);
    debounceTimers.delete(entidad);
    dispatch({ type: LIST_UNLOAD, payload: { entidad } });
};

// ============================================================
// Acciones de paginacion (convertidas a thunks para servidor)
// ============================================================

export const setVisiblesList = (entidad, data) => (dispatch, getState) => {
    dispatch({
        type: LIST_SETVISIBLES,
        payload: { data, entidad }
    });
    // En modo servidor NO se hace fetch — solo repagina en cliente
    // El reducer recalcula paginada dentro del chunk actual
};

export const setPageList = (entidad, data) => (dispatch, getState) => {
    const entityState = getState().list[entidad];

    if (entityState && entityState.serverPaged) {
        const { visibles, serverPageSize, serverPage } = entityState;
        const globalPage = data;
        const pagesPerChunk = Math.ceil(serverPageSize / visibles);
        const targetChunk = Math.floor(globalPage / pagesPerChunk);
        const clientPage = globalPage - targetChunk * pagesPerChunk;

        if (targetChunk === serverPage) {
            // Mismo chunk: paginación local
            dispatch({
                type: LIST_SETPAGE,
                payload: { data: clientPage, entidad }
            });
        } else {
            // Chunk diferente: actualizar serverPage y fetch
            dispatch({
                type: LIST_SERVER_STATE,
                payload: { entidad, serverPage: targetChunk }
            });
            dispatch(fetchServerPage(entidad, false, clientPage));
        }
        return;
    }

    dispatch({ type: LIST_SETPAGE, payload: { data, entidad } });
};

export const firstPageList = entidad => (dispatch, getState) => {
    const entityState = getState().list[entidad];

    if (entityState && entityState.serverPaged) {
        if (entityState.serverPage === 0) {
            dispatch({ type: LIST_FIRST_PAGE, payload: { entidad } });
        } else {
            dispatch({
                type: LIST_SERVER_STATE,
                payload: { entidad, serverPage: 0 }
            });
            dispatch(fetchServerPage(entidad, false, 0));
        }
        return;
    }

    dispatch({ type: LIST_FIRST_PAGE, payload: { entidad } });
};

export const previousPageList = entidad => (dispatch, getState) => {
    const entityState = getState().list[entidad];

    if (entityState && entityState.serverPaged) {
        if (entityState.page > 0) {
            // Dentro del chunk: paginación local
            dispatch({
                type: LIST_PREVIOUS_PAGE,
                payload: { entidad }
            });
        } else if (entityState.serverPage > 0) {
            // Cruzar al chunk anterior
            const lastClientPage =
                Math.ceil(entityState.serverPageSize / entityState.visibles) -
                1;
            dispatch({
                type: LIST_SERVER_STATE,
                payload: {
                    entidad,
                    serverPage: entityState.serverPage - 1
                }
            });
            dispatch(fetchServerPage(entidad, false, lastClientPage));
        }
        return;
    }

    dispatch({ type: LIST_PREVIOUS_PAGE, payload: { entidad } });
};

export const nextPageList = entidad => (dispatch, getState) => {
    const entityState = getState().list[entidad];

    if (entityState && entityState.serverPaged) {
        const maxClientPage = Math.max(
            0,
            Math.ceil(entityState.list.length / entityState.visibles) - 1
        );

        if (entityState.page < maxClientPage) {
            // Dentro del chunk: paginación local
            dispatch({
                type: LIST_NEXT_PAGE,
                payload: { entidad }
            });
        } else {
            // Cruzar al chunk siguiente
            const globalEndItem =
                (entityState.serverPage + 1) * entityState.serverPageSize;
            if (globalEndItem < entityState.serverTotal) {
                dispatch({
                    type: LIST_SERVER_STATE,
                    payload: {
                        entidad,
                        serverPage: entityState.serverPage + 1
                    }
                });
                dispatch(fetchServerPage(entidad, false, 0));
            }
        }
        return;
    }

    dispatch({ type: LIST_NEXT_PAGE, payload: { entidad } });
};

export const lastPageList = entidad => (dispatch, getState) => {
    const entityState = getState().list[entidad];

    if (entityState && entityState.serverPaged) {
        const lastChunk = Math.max(
            0,
            Math.ceil(entityState.serverTotal / entityState.serverPageSize) - 1
        );

        if (entityState.serverPage === lastChunk) {
            dispatch({
                type: LIST_LAST_PAGE,
                payload: { entidad }
            });
        } else {
            dispatch({
                type: LIST_SERVER_STATE,
                payload: { entidad, serverPage: lastChunk }
            });
            dispatch(fetchServerPage(entidad, false, -1));
        }
        return;
    }

    dispatch({ type: LIST_LAST_PAGE, payload: { entidad } });
};

// ============================================================
// Seleccion (sin cambios)
// ============================================================

export const selectAllList =
    (entidad, data, multiple, action) => (dispatch, getState) => {
        dispatch({
            type: LIST_SELECT_ALL,
            payload: { entidad, data }
        });
        action && action();
    };

export const selectOneList =
    (entidad, data, multiple, action) => (dispatch, getState) => {
        dispatch({
            type: LIST_SELECT_ONE,
            payload: { entidad, data, multiple }
        });
        action && action();
    };

export const selectSomeList =
    (entidad, data, action) => (dispatch, getState) => {
        dispatch({
            type: LIST_SELECT_SOME,
            payload: { entidad, data }
        });
        action && action();
    };

// ============================================================
// Nuevas acciones para modo servidor
// ============================================================

export const sortList = (entidad, fields) => (dispatch, getState) => {
    const entityState = getState().list[entidad];
    if (!entityState || !entityState.serverPaged) return;

    dispatch({
        type: LIST_SERVER_STATE,
        payload: {
            entidad,
            serverSort: Array.isArray(fields) ? fields : [fields],
            serverPage: 0
        }
    });
    dispatch(fetchServerPage(entidad, false, 0));
};

export const searchList = (entidad, term) => (dispatch, getState) => {
    const entityState = getState().list[entidad];
    if (!entityState || !entityState.serverPaged) return;

    dispatch({
        type: LIST_SERVER_STATE,
        payload: { entidad, serverSearch: term || '', serverPage: 0 }
    });
    debouncedFetch(entidad, () => {
        dispatch(fetchServerPage(entidad, true, 0));
    });
};

// ============================================================
// Acciones genericas (sin cambios)
// ============================================================
/*
export const reorderList =
    (entidad, id, start, end) => (dispatch, getState) => {
        if (start === end) return;

        const idDestino = getList(entidad, getState())[end].id;

        // Reordeno la lista a nivel de cliente
        dispatch({ type: LIST_REORDER, payload: { entidad, id, idDestino } });

        // Actualizo el orden en el servidor
        dispatch({ type: LIST_UPDATING, payload: { entidad } });

        // Obtengo el token
        const token = getToken(getState());
        const api = getListApi(entidad, getState());

        // Creo los parametros de la reordenacion
        const params = {
            start: id,
            end: idDestino,
            asc: end < start
        };

        // Hago el Post sin actualizar la lista con los datos devueltos
        fetch(`${config.servidor}:${config.puerto}/api/${api}/reorder`, {
            method: 'POST',
            body: JSON.stringify(params),
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
            .then(response => {
                return response.json();
            })
            .then(data => {
                if (data.code !== undefined) {
                    dispatch({
                        type: LIST_FAILED,
                        payload: { error: data, entidad }
                    });
                } else {
                    console.log('ok');
                    dispatch({
                        type: LIST_SAVED,
                        payload: { data: null, entidad }
                    });
                }
            })
            .catch(error => {
                dispatch({
                    type: LIST_FAILED,
                    payload: {
                        error: {
                            code: 503,
                            message: 'El servicio no esta disponible'
                        },
                        entidad
                    }
                });
            });
    };
*/
export const execList =
    (entidad, action, postActions, onlyData) => (dispatch, getState) => {
        // Inicio la actualizacion
        if (!onlyData) dispatch({ type: LIST_UPDATING, payload: { entidad } });

        // Obtengo el token
        const token = getToken(getState());

        // accion para el servidor
        fetch(`${config.servidor}:${config.puerto}/api/${action.api}`, {
            method: action.metodo,
            body: JSON.stringify(action.data),
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
            .then(response => {
                return response.json();
            })
            .then(
                data => {
                    if (onlyData) {
                        if (postActions !== undefined) {
                            postActions.forEach(accion => accion(data));
                        }
                    } else {
                        if (data.code !== undefined) {
                            dispatch({
                                type: LIST_FAILED,
                                payload: { error: data, entidad }
                            });
                        } else {
                            //console.log(data)
                            dispatch({
                                type: LIST_SAVED,
                                payload: { data: null, entidad }
                            });
                            // Ejecuto las acciones posteriores
                            if (postActions !== undefined) {
                                postActions.map(accion => accion());
                            }
                        }
                    }
                },
                error => {
                    dispatch({
                        type: LIST_FAILED,
                        payload: {
                            error: {
                                code: 503,
                                message: 'El servicio no esta disponible'
                            },
                            entidad
                        }
                    });
                }
            );
    };
